import React, { useState } from "react";
import "./Login.css";

// Environment Configuration
const USER_BASE_URL = process.env.REACT_APP_USER_API_URL;
const NOTIFICATION_BASE_URL = process.env.REACT_APP_NOTIFICATION_API_URL;

// Authentication Configuration (Optional - currently disabled)
const AUTH_USERNAME = process.env.REACT_APP_AUTH_USERNAME;
const AUTH_PASSWORD = process.env.REACT_APP_AUTH_PASSWORD;
const ENABLE_AUTH = AUTH_USERNAME && AUTH_PASSWORD;

// Timeout and Retry Configuration
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 10000; // 10 seconds
const REDIRECT_DELAY = parseInt(process.env.REACT_APP_REDIRECT_DELAY) || 2000; // 2 seconds
const LOGIN_SUCCESS_DELAY =
  parseInt(process.env.REACT_APP_LOGIN_SUCCESS_DELAY) || 1500; // 1.5 seconds

// Debug Configuration
const DEBUG_MODE = process.env.REACT_APP_DEBUG_MODE === "true" || false;

// Feature Configuration
const ENABLE_DISPOSABLE_EMAIL_CHECK =
  process.env.REACT_APP_ENABLE_DISPOSABLE_EMAIL_CHECK !== "false";

// Disposable email domains (configurable via environment)
const DISPOSABLE_DOMAINS = process.env.REACT_APP_DISPOSABLE_DOMAINS
  ? process.env.REACT_APP_DISPOSABLE_DOMAINS.split(",")
  : [
      "10minutemail.com",
      "tempmail.org",
      "guerrillamail.com",
      "mailinator.com",
      "throwaway.email",
      "temp-mail.org",
      "yopmail.com",
      "maildrop.cc",
      "sharklasers.com",
    ];

// Environment validation and debug logging
const validateEnvironment = () => {
  const missing = [];

  if (!USER_BASE_URL) missing.push("REACT_APP_USER_API_URL");
  if (!NOTIFICATION_BASE_URL) missing.push("REACT_APP_NOTIFICATION_API_URL");

  // Authentication is now optional
  if (ENABLE_AUTH && !AUTH_USERNAME) missing.push("REACT_APP_AUTH_USERNAME");
  if (ENABLE_AUTH && !AUTH_PASSWORD) missing.push("REACT_APP_AUTH_PASSWORD");

  if (missing.length > 0) {
    console.error("Missing required environment variables:", missing);
    console.error(
      "Please check your .env file. See .env.example for reference.",
    );
  }

  return missing.length === 0;
};

// Validate environment on component load
const isEnvironmentValid = validateEnvironment();

// Debug logging
if (DEBUG_MODE) {
  console.log("Login Environment Configuration:", {
    USER_BASE_URL,
    NOTIFICATION_BASE_URL,
    ENABLE_AUTH,
    AUTH_USERNAME: AUTH_USERNAME ? "***configured***" : "disabled",
    AUTH_PASSWORD: AUTH_PASSWORD ? "***configured***" : "disabled",
    API_TIMEOUT,
    REDIRECT_DELAY,
    LOGIN_SUCCESS_DELAY,
    ENABLE_DISPOSABLE_EMAIL_CHECK,
    DISPOSABLE_DOMAINS_COUNT: DISPOSABLE_DOMAINS.length,
    ENVIRONMENT_VALID: isEnvironmentValid,
  });
}

const Login = ({ onLoginSuccess, onRegisterRedirect }) => {
  const [formData, setFormData] = useState({
    email: "",
    otp: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [message, setMessage] = useState("");

  // Advanced email validation
  const validateEmailAdvanced = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      return "Email is required";
    }

    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }

    // Check for disposable email domains (if enabled)
    if (ENABLE_DISPOSABLE_EMAIL_CHECK) {
      const domain = email.split("@")[1]?.toLowerCase();
      if (DISPOSABLE_DOMAINS.includes(domain)) {
        if (DEBUG_MODE) {
          console.log("Disposable email domain detected:", domain);
        }
        return "Please use a permanent email address";
      }
    }

    return "";
  };

  const validateOtp = (otp) => {
    if (!otp) {
      return "OTP is required";
    }
    if (!/^\d{6}$/.test(otp)) {
      return "OTP must be 6 digits";
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (DEBUG_MODE) {
      console.log("Input change:", { name, value, otpSent });
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const checkUserExists = async (email) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      // Build headers conditionally
      const headers = {
        "Content-Type": "application/json",
      };

      // Add authentication if enabled
      if (ENABLE_AUTH) {
        headers["Authorization"] =
          "Basic " + btoa(`${AUTH_USERNAME}:${AUTH_PASSWORD}`);
      }

      const response = await fetch(
        `${USER_BASE_URL}/user/email/${encodeURIComponent(email)}`,
        {
          method: "GET",
          headers,
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (response.ok) {
        const userData = await response.json();
        console.log("=== User data from API ===");
        console.log("Full userData:", userData);
        console.log("userData.email:", userData.email);
        console.log("userData.firstName:", userData.firstName);
        console.log("userData.phone:", userData.phone);
        return { exists: true, user: userData };
      } else if (response.status === 404) {
        if (DEBUG_MODE) {
          console.log("User not found for email:", email);
        }
        return { exists: false, user: null };
      } else if (response.status === 400) {
        throw new Error("Invalid email format provided");
      } else {
        const errorText = await response.text().catch(() => "Unknown error");
        throw new Error(
          `Failed to verify user: ${response.status} - ${errorText}`,
        );
      }
    } catch (error) {
      if (error.name === "AbortError") {
        console.error("User check request timed out");
        throw new Error("Request timed out. Please check your connection.");
      }
      console.error("Error checking user:", error);
      throw error;
    }
  };

  const sendOtp = async (email) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const response = await fetch(
        `${NOTIFICATION_BASE_URL}/notification/send-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to send OTP: ${response.status} ${response.statusText}`,
        );
      }

      const result = await response.json();
      if (DEBUG_MODE) {
        console.log("OTP sent successfully:", result);
      }
      return result;
    } catch (error) {
      if (error.name === "AbortError") {
        console.error("OTP send request timed out");
        throw new Error("Request timed out. Please check your connection.");
      }
      console.error("Error sending OTP:", error);
      throw error;
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      const response = await fetch(
        `${NOTIFICATION_BASE_URL}/notification/verify-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, otp }),
          signal: controller.signal,
        },
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Invalid OTP: ${response.status} ${response.statusText}`,
        );
      }

      const result = await response.json();
      if (DEBUG_MODE) {
        console.log("OTP verified successfully:", result);
      }
      return result;
    } catch (error) {
      if (error.name === "AbortError") {
        console.error("OTP verification request timed out");
        throw new Error("Request timed out. Please check your connection.");
      }
      console.error("Error verifying OTP:", error);
      throw error;
    }
  };

  const handleSendOtp = async () => {
    const emailError = validateEmailAdvanced(formData.email);
    if (emailError) {
      setErrors({ email: emailError });
      return;
    }

    setLoading(true);
    setMessage("");
    setErrors({});

    try {
      if (DEBUG_MODE) {
        console.log(
          "Starting user verification process for email:",
          formData.email,
        );
      }

      // Check if user exists
      const { exists, user } = await checkUserExists(formData.email);
      setUserExists(exists);

      if (DEBUG_MODE) {
        // console.log('User verification result:', { exists, userFound: !!user });
      }

      if (exists) {
        // Send OTP for existing user
        await sendOtp(formData.email);
        setOtpSent(true);
        setMessage("OTP sent to your registered email address");
        console.log("=== User found in login ===");
        console.log("User object:", user);
        console.log("User keys:", Object.keys(user));
        console.log("User.email:", user.email);
        console.log("Stringified user:", JSON.stringify(user, null, 2));
        window.sessionStorage.setItem("existingUser", JSON.stringify(user));
      } else {
        // Redirect to registration for new user
        setMessage("User not found. Redirecting to registration...");
        setTimeout(() => {
          onRegisterRedirect(formData.email);
        }, REDIRECT_DELAY);
      }
    } catch (error) {
      const errorMessage =
        error.message || "Failed to process request. Please try again.";
      setErrors({ email: errorMessage });
      if (DEBUG_MODE) {
        console.error("Login process error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otpError = validateOtp(formData.otp);
    if (otpError) {
      setErrors({ otp: otpError });
      return;
    }

    setLoading(true);
    setMessage("");
    setErrors({});

    try {
      await verifyOtp(formData.email, formData.otp);
      const existingUser = JSON.parse(
        window.sessionStorage.getItem("existingUser") || "{}",
      );

      console.log("=== OTP verified, passing user to App ===");
      console.log("Retrieved from sessionStorage:", existingUser);
      console.log("existingUser keys:", Object.keys(existingUser));
      console.log("existingUser.email:", existingUser.email);

      setMessage("Login successful!");
      setTimeout(() => {
        console.log("Calling onLoginSuccess with:", existingUser);
        onLoginSuccess(existingUser);
      }, LOGIN_SUCCESS_DELAY);
    } catch (error) {
      const errorMessage =
        error.message || "Invalid or expired OTP. Please try again.";
      setErrors({ otp: errorMessage });
      if (DEBUG_MODE) {
        console.error("OTP verification error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!formData.email) return;

    setLoading(true);
    try {
      await sendOtp(formData.email);
      setMessage("OTP resent successfully");
    } catch (error) {
      setMessage("Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  async function invoiceDownload() {
    const invoiceBtn = document.getElementById("invoiceEmail")
    const email = invoiceBtn.value.trim();

    if (!email) {
      alert("Please Enter your Registered email id");
      return;
    }

    const originalBtnContent = invoiceBtn.innerHTML;

    // Loading state
    invoiceBtn.disabled = true;
    invoiceBtn.innerHTML = `
    <span class="invoice-spinner"></span>
    <span>Downloading...</span>
  `;

    try {
      const res = await fetch("invoice-download.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
        }),
      });

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const result = await res.json();

      if (!result || typeof result !== "object") {
        throw new Error("Invalid response from server.");
      }

      if (!result.status) {
        throw new Error(result.message || "Invoice download failed.");
      }

      const fileName = result.data.file_name;

      if (!fileName) {
        throw new Error("Invoice file not found.");
      }

      // Create download link
      const anchor = document.createElement("a");

      anchor.href = `https://gtek4u.com/invoices_holder/${fileName}`;
      anchor.download = fileName;
      anchor.style.display = "none";

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      console.error("Invoice download error:", error);
      alert(error.message || "Something went wrong.");
    } finally {
      // Restore button
      invoiceBtn.disabled = false;
      invoiceBtn.innerHTML = originalBtnContent;
    }
  }

  function toggleModal() {
    // const modal = document.querySelector(".invoice-card");
    // if (modal.style.display === "none" || modal.style.display === "") {
    //   modal.style.display = "block";
    // } else {
    //   modal.style.display = "none";
    // }
  }

  const handleChangeEmail = () => {
    // Reset form state to allow email editing
    setOtpSent(false);
    setUserExists(false);
    setFormData((prev) => ({
      ...prev,
      otp: "",
    }));
    setMessage("");
    setErrors({});

    if (DEBUG_MODE) {
      console.log("Resetting form to allow email change");
    }
  };

  // Environment validation check
  if (!isEnvironmentValid) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <h2>Configuration Error</h2>
            <p className="error-message">
              Missing required environment variables. Please check the console
              and .env file.
            </p>
          </div>
          <div className="message error">
            <p>
              <strong>Required Environment Variables:</strong>
            </p>
            <ul style={{ textAlign: "left", marginTop: "10px" }}>
              <li>REACT_APP_USER_API_URL</li>
              <li>REACT_APP_NOTIFICATION_API_URL</li>
            </ul>
            <p style={{ marginTop: "10px", fontSize: "0.9em" }}>
              <em>Authentication is currently disabled for microservices.</em>
            </p>
            <p style={{ marginTop: "15px" }}>
              See <code>.env.example</code> for reference configuration.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  function openModal() {
    setShowInvoiceModal(true);
  }

  function closeModal() {
    setShowInvoiceModal(false);
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h2>Conference Login</h2>
          <p>Enter your email to continue</p>
        </div>

        <form className="login-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className={`form-control ${errors.email ? "error" : ""}`}
              value={formData.email}
              onChange={handleInputChange}
              disabled={otpSent}
              placeholder="Enter your email address"
              autoComplete="email"
            />
            {errors.email && (
              <span className="error-message">{errors.email}</span>
            )}
          </div>

          {!otpSent && (
            <button
              type="button"
              className="btn btn-primary login-btn"
              onClick={handleSendOtp}
              disabled={loading || !formData.email}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Processing...
                </>
              ) : (
                "Continue"
              )}
            </button>
          )}

          {otpSent && (
            <>
              <div className="form-group">
                <label htmlFor="otp" className="form-label">
                  Enter OTP <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="otp"
                  name="otp"
                  className={`form-control otp-input ${errors.otp ? "error" : ""}`}
                  value={formData.otp}
                  onChange={handleInputChange}
                  placeholder="Enter 6-digit OTP"
                  maxLength="6"
                  autoComplete="one-time-code"
                />
                {errors.otp && (
                  <span className="error-message">{errors.otp}</span>
                )}
              </div>

              <div className="otp-actions">
                <button
                  type="button"
                  className="btn btn-primary login-btn"
                  onClick={handleVerifyOtp}
                  disabled={loading || !formData.otp}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Verifying...
                    </>
                  ) : (
                    "Verify & Login"
                  )}
                </button>

                <button
                  type="button"
                  className="btn btn-secondary resend-btn"
                  onClick={handleResendOtp}
                  disabled={loading}
                >
                  Resend OTP
                </button>

                <button
                  type="button"
                  className="btn btn-outline change-email-btn"
                  onClick={handleChangeEmail}
                  disabled={loading}
                >
                  Change Email
                </button>
              </div>
            </>
          )}

          {message && (
            <div
              className={`message ${message.includes("successful") || message.includes("sent") ? "success" : "info"}`}
            >
              {message}
            </div>
          )}
        </form>

        <div className="login-footer">
          <p>New to our conference?</p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
            }}
          >
            <button
              type="button"
              className="btn btn-link"
              onClick={() => onRegisterRedirect()}
            >
              Register Now
            </button>

            {/* <button
              type="button"
              className="btn btn-link"
              id="invoiceBtn"
              onClick={openModal}
            >
              Download Invoice
            </button> */}
          </div>
        </div>
      </div>

      {showInvoiceModal && (
        <div className="invoice-card">
          <button className="close-btn" onClick={closeModal}>
            X
          </button>

          <label>
            Email Address <span style={{ color: "red" }}>*</span>
          </label>
          <input
            placeholder="Please Enter your Registered Email Id"
            id="invoiceEmail"
          />

          <button
            className="btn btn-primary login-btn"
            onClick={invoiceDownload}
          >
            Download
          </button>
        </div>
      )}
    </div>
  );
};

export default Login;
